import { useMemo, useState } from "react";
import { Clock3, MoonStar, PencilLine, Plus, Trash2 } from "lucide-react";

import {
  buildHistoryDaySections,
  formatDurationCompact,
  timeEntryCrossesMidnight,
  type TimeEntry,
} from "@zentrack/domain";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  PageHeader,
} from "@zentrack/ui";

import { useWebApp } from "../app/app-provider";
import { EntryEditorDialog, type EntryEditorValues } from "../components/entry-editor-dialog";
import { ConfirmDialog } from "../components/confirm-dialog";
import { projectLabel } from "../components/shared";
import { formatDateTime, formatDayHeading } from "../lib/date-helpers";

export function HistoryRoute() {
  const { api, perform, setErrorMessage, snapshot } = useWebApp();
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<TimeEntry | null>(null);

  const sections = useMemo(
    () => (snapshot ? buildHistoryDaySections(snapshot.timeEntries) : []),
    [snapshot?.timeEntries],
  );

  if (!snapshot) {
    return null;
  }

  const handleSave = async (values: EntryEditorValues) => {
    setErrorMessage(null);
    if (editingEntry) {
      await perform(() =>
        api.history.updateManualEntry({
          id: editingEntry.id,
          ...values,
        }),
      );
    } else {
      await perform(() => api.history.createManualEntry(values));
    }
    setEntryDialogOpen(false);
    setEditingEntry(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingEntry) return;
    setErrorMessage(null);
    await perform(() => api.history.deleteEntry({ id: deletingEntry.id }));
    setDeletingEntry(null);
  };

  return (
    <div className="flex flex-col gap-5 pb-10">
      <PageHeader
        actions={
          <Button
            onClick={() => {
              setEditingEntry(null);
              setEntryDialogOpen(true);
            }}
            type="button"
            variant="outline"
          >
            <Plus className="size-4" />
            Add entry
          </Button>
        }
        description="Review and edit finished sessions."
        title="History"
      />

      {sections.length === 0 ? (
        <EmptyState
          description="Start and stop the timer to build your first session timeline."
          icon={Clock3}
          title="No history yet"
        />
      ) : (
        sections.map((section) => (
          <Card key={section.date}>
            <CardHeader className="flex-row items-center justify-between border-b border-border/60 pb-3">
              <p className="text-sm font-medium text-foreground">{formatDayHeading(section.date)}</p>
              <p className="text-sm font-medium tabular-nums text-muted-foreground">
                {formatDurationCompact(section.totalCountedDurationSeconds)}
              </p>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="divide-y divide-border/50">
                {section.entries.map((entry) => {
                  const isOvernight = timeEntryCrossesMidnight(entry);
                  const spanSeconds = Math.round(
                    (new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 1000,
                  );
                  const isFiltered = entry.countedDurationSeconds < spanSeconds;
                  const entryAppUsages = snapshot.appUsages
                    .filter((usage) => usage.timeEntryId === entry.id)
                    .sort((left, right) => left.startTime.localeCompare(right.startTime));

                  return (
                    <div key={entry.id}>
                      <div className="flex items-start justify-between gap-4 py-3">
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <p className="text-sm font-medium text-foreground">
                              {projectLabel(snapshot, entry.projectId)}
                            </p>
                            {isFiltered ? <Badge tone="warning">Filtered</Badge> : null}
                            {isOvernight ? (
                              <Badge tone="neutral">
                                <MoonStar className="size-3" />
                                Overnight
                              </Badge>
                            ) : null}
                          </div>
                          {entry.note ? (
                            <p className="text-xs text-muted-foreground">{entry.note}</p>
                          ) : null}
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(entry.startTime)} –{" "}
                            {isOvernight
                              ? formatDateTime(entry.endTime)
                              : formatDateTime(entry.endTime, {
                                  dateStyle: undefined,
                                  timeStyle: "short",
                                })}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <p className="text-sm font-medium tabular-nums text-foreground">
                            {formatDurationCompact(entry.countedDurationSeconds)}
                          </p>
                          <Button
                            onClick={() => {
                              setEditingEntry(entry);
                              setEntryDialogOpen(true);
                            }}
                            size="sm"
                            type="button"
                            variant="ghost"
                          >
                            <PencilLine className="size-3.5" />
                          </Button>
                          <Button
                            onClick={() => setDeletingEntry(entry)}
                            size="sm"
                            type="button"
                            variant="ghost"
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      </div>
                      {entryAppUsages.length > 0 ? (
                        <div className="mb-3 rounded-md border border-border/50 bg-secondary/30">
                          <p className="border-b border-border/40 px-3 py-2 text-[11px] font-medium text-muted-foreground">
                            App usage
                          </p>
                          <div className="divide-y divide-border/40">
                            {entryAppUsages.map((usage) => (
                              <div
                                className="flex items-center justify-between gap-4 px-3 py-2"
                                key={usage.id}
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <Badge tone={usage.countsTowardTrackedTime ? "accent" : "neutral"}>
                                    {usage.countsTowardTrackedTime ? "Counted" : "Ignored"}
                                  </Badge>
                                  <span className="truncate text-xs text-foreground">
                                    {usage.appName}
                                  </span>
                                </div>
                                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                  {formatDurationCompact(
                                    Math.max(
                                      0,
                                      Math.round(
                                        (new Date(usage.endTime).getTime() -
                                          new Date(usage.startTime).getTime()) /
                                          1000,
                                      ),
                                    ),
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      <EntryEditorDialog
        defaultProjectId={snapshot.projects[0]?.id}
        entry={editingEntry}
        onOpenChange={(open) => {
          setEntryDialogOpen(open);
          if (!open) {
            setEditingEntry(null);
          }
        }}
        onSave={handleSave}
        open={entryDialogOpen}
        projectLabel={(value) => projectLabel(snapshot, value)}
        projects={snapshot.projects}
      />

      <ConfirmDialog
        confirmLabel="Delete entry"
        destructive
        description={
          deletingEntry
            ? `This will permanently delete the entry for "${projectLabel(
                snapshot,
                deletingEntry.projectId,
              )}"${deletingEntry.note ? ` (${deletingEntry.note})` : ""}. This cannot be undone.`
            : ""
        }
        onConfirm={handleDeleteConfirm}
        onOpenChange={(open) => {
          if (!open) setDeletingEntry(null);
        }}
        open={Boolean(deletingEntry)}
        title="Delete entry"
      />
    </div>
  );
}
